/**
 * ごみ種別設定サービス（統合版）
 * 本番API、モックAPI、環境切り替えを一つのファイルで管理
 */

import { USE_MOCK_API, API_BASE_URL } from '../config.js'

// =============================================================================
// モックデータとAPI
// =============================================================================

// モックデータ
let mockWasteTypes = [
  {
    id: 1,
    typeName: '燃えるごみ'
  },
  {
    id: 2,
    typeName: '燃えないごみ'
  },
  {
    id: 3,
    typeName: '資源ごみ'
  },
  {
    id: 4,
    typeName: '粗大ごみ'
  },
  {
    id: 5,
    typeName: 'プラスチックごみ'
  },
  {
    id: 6,
    typeName: '有害ごみ'
  }
]

let nextId = mockWasteTypes.length + 1

// =============================================================================
// モックAPI実装
// =============================================================================

/**
 * モック: ごみ種別一覧を取得
 * @returns {Promise<Object>} API応答
 */
async function mockGetWasteTypes() {
  // 実際のAPI遅延をシミュレート
  await new Promise(resolve => setTimeout(resolve, 300))

  try {
    // ID順でソート
    const sortedTypes = [...mockWasteTypes].sort((a, b) => a.id - b.id)

    return {
      success: true,
      data: sortedTypes,
      message: 'ごみ種別一覧を取得しました'
    }
  } catch (error) {
    return {
      success: false,
      data: [],
      message: 'ごみ種別一覧の取得に失敗しました'
    }
  }
}

/**
 * モック: 新しいごみ種別を作成
 * @param {Object} typeData - 種別データ
 * @param {string} typeData.typeName - 種別名
 * @returns {Promise<Object>} API応答
 */
async function mockCreateWasteType(typeData) {
  // 実際のAPI遅延をシミュレート
  await new Promise(resolve => setTimeout(resolve, 500))

  try {
    // バリデーション
    if (!typeData.typeName || typeData.typeName.trim() === '') {
      return {
        success: false,
        data: null,
        message: '種別名は必須です'
      }
    }

    const trimmedName = typeData.typeName.trim()

    // 文字数チェック
    if (trimmedName.length > 20) {
      return {
        success: false,
        data: null,
        message: '種別名は20文字以内で入力してください'
      }
    }

    // 重複チェック
    const isDuplicate = mockWasteTypes.some(type =>
      type.typeName === trimmedName
    )

    if (isDuplicate) {
      return {
        success: false,
        data: null,
        message: 'この種別名は既に存在します'
      }
    }

    // 新しい種別を作成
    const newType = {
      id: nextId++,
      typeName: trimmedName
    }

    mockWasteTypes.push(newType)

    // 更新された一覧を返す
    const sortedTypes = [...mockWasteTypes].sort((a, b) => a.id - b.id)

    return {
      success: true,
      data: sortedTypes,
      message: `ごみ種別「${trimmedName}」が追加されました`
    }
  } catch (error) {
    return {
      success: false,
      data: null,
      message: 'ごみ種別の作成に失敗しました'
    }
  }
}

/**
 * モック: ごみ種別を更新
 * @param {number} id - 種別ID
 * @param {Object} typeData - 更新データ
 * @param {string} typeData.typeName - 種別名
 * @returns {Promise<Object>} API応答
 */
async function mockUpdateWasteType(id, typeData) {
  // 実際のAPI遅延をシミュレート
  await new Promise(resolve => setTimeout(resolve, 500))

  try {
    // バリデーション
    if (!typeData.typeName || typeData.typeName.trim() === '') {
      return {
        success: false,
        data: null,
        message: '種別名は必須です'
      }
    }

    const trimmedName = typeData.typeName.trim()

    // 文字数チェック
    if (trimmedName.length > 20) {
      return {
        success: false,
        data: null,
        message: '種別名は20文字以内で入力してください'
      }
    }

    // 更新対象を検索
    const targetIndex = mockWasteTypes.findIndex(type => type.id === id)
    if (targetIndex === -1) {
      return {
        success: false,
        data: null,
        message: '指定された種別が見つかりません'
      }
    }

    // 重複チェック（自分以外）
    const isDuplicate = mockWasteTypes.some(type =>
      type.id !== id && type.typeName === trimmedName
    )

    if (isDuplicate) {
      return {
        success: false,
        data: null,
        message: 'この種別名は既に存在します'
      }
    }

    // 種別を更新
    mockWasteTypes[targetIndex] = {
      ...mockWasteTypes[targetIndex],
      typeName: trimmedName
    }

    // 更新された一覧を返す
    const sortedTypes = [...mockWasteTypes].sort((a, b) => a.id - b.id)

    return {
      success: true,
      data: sortedTypes,
      message: `ごみ種別「${trimmedName}」が更新されました`
    }
  } catch (error) {
    return {
      success: false,
      data: null,
      message: 'ごみ種別の更新に失敗しました'
    }
  }
}

/**
 * モック: ごみ種別を削除
 * @param {number} id - 種別ID
 * @returns {Promise<Object>} API応答
 */
async function mockDeleteWasteType(id) {
  // 実際のAPI遅延をシミュレート
  await new Promise(resolve => setTimeout(resolve, 400))

  try {
    // 削除対象を検索
    const targetIndex = mockWasteTypes.findIndex(type => type.id === id)
    if (targetIndex === -1) {
      return {
        success: false,
        data: null,
        message: '指定された種別が見つかりません'
      }
    }

    const deletedType = mockWasteTypes[targetIndex]

    // 種別を削除
    mockWasteTypes.splice(targetIndex, 1)

    // 更新された一覧を返す
    const sortedTypes = [...mockWasteTypes].sort((a, b) => a.id - b.id)

    return {
      success: true,
      data: sortedTypes,
      message: `ごみ種別「${deletedType.typeName}」が削除されました`
    }
  } catch (error) {
    return {
      success: false,
      data: null,
      message: 'ごみ種別の削除に失敗しました'
    }
  }
}

// =============================================================================
// 本番API実装
// =============================================================================

/**
 * 本番: ごみ種別一覧を取得
 * @returns {Promise<Object>} API応答
 */
async function productionGetWasteTypes() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/system-settings/waste-types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    return {
      success: true,
      data: result,
      message: 'ごみ種別一覧を取得しました'
    }
  } catch (error) {
    return {
      success: false,
      data: [],
      message: `ごみ種別一覧の取得に失敗しました: ${error.message}`
    }
  }
}

/**
 * 本番: 新しいごみ種別を作成
 * @param {Object} typeData - 種別データ
 * @returns {Promise<Object>} API応答
 */
async function productionCreateWasteType(typeData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/system-settings/waste-types`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(typeData)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    return {
      success: true,
      data: result,
      message: 'ごみ種別が作成されました'
    }
  } catch (error) {
    return {
      success: false,
      data: null,
      message: `ごみ種別の作成に失敗しました: ${error.message}`
    }
  }
}

/**
 * 本番: ごみ種別を更新
 * @param {number} id - 種別ID
 * @param {Object} typeData - 更新データ
 * @returns {Promise<Object>} API応答
 */
async function productionUpdateWasteType(id, typeData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/system-settings/waste-types/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(typeData)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    return {
      success: true,
      data: result,
      message: 'ごみ種別が更新されました'
    }
  } catch (error) {
    return {
      success: false,
      data: null,
      message: `ごみ種別の更新に失敗しました: ${error.message}`
    }
  }
}

/**
 * 本番: ごみ種別を削除
 * @param {number} id - 種別ID
 * @returns {Promise<Object>} API応答
 */
async function productionDeleteWasteType(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/system-settings/waste-types/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    return {
      success: true,
      data: null,
      message: 'ごみ種別が削除されました'
    }
  } catch (error) {
    return {
      success: false,
      data: null,
      message: `ごみ種別の削除に失敗しました: ${error.message}`
    }
  }
}

// =============================================================================
// 統合API関数（モック/本番の切り替え）
// =============================================================================

/**
 * ごみ種別一覧を取得
 * @returns {Promise<Object>} API応答
 */
export async function getWasteTypes() {
  if (USE_MOCK_API) {
    return await mockGetWasteTypes()
  } else {
    return await productionGetWasteTypes()
  }
}

/**
 * 新しいごみ種別を作成
 * @param {Object} typeData - 種別データ
 * @param {string} typeData.typeName - 種別名
 * @returns {Promise<Object>} API応答
 */
export async function createWasteType(typeData) {
  // データの前処理
  const processedData = {
    typeName: typeData.typeName ? typeData.typeName.trim() : ''
  }

  if (USE_MOCK_API) {
    return await mockCreateWasteType(processedData)
  } else {
    return await productionCreateWasteType(processedData)
  }
}

/**
 * ごみ種別を更新
 * @param {number} id - 種別ID
 * @param {Object} typeData - 更新データ
 * @param {string} typeData.typeName - 種別名
 * @returns {Promise<Object>} API応答
 */
export async function updateWasteType(id, typeData) {
  // データの前処理
  const processedData = {
    typeName: typeData.typeName ? typeData.typeName.trim() : ''
  }

  if (USE_MOCK_API) {
    return await mockUpdateWasteType(id, processedData)
  } else {
    return await productionUpdateWasteType(id, processedData)
  }
}

/**
 * ごみ種別を削除
 * @param {number} id - 種別ID
 * @returns {Promise<Object>} API応答
 */
export async function deleteWasteType(id) {
  if (USE_MOCK_API) {
    return await mockDeleteWasteType(id)
  } else {
    return await productionDeleteWasteType(id)
  }
}

// デフォルトエクスポート
export default {
  getWasteTypes,
  createWasteType,
  updateWasteType,
  deleteWasteType
}
